import { isNil, toNumber } from 'lodash';

import { getDecimalSafeValue } from 'utils/mathUtils';
import { FinancialInfoFormValues } from './FinancialInfo';
import {
  CustomerMspOrVmsOverviewDto,
  PriceAgreementW2InfoResponseModel,
  AssignmentTypeEnum,
  BOUsersPriceAgreementRespModel,
} from 'api/backofficeApiClient';
import { WorkerInfoFormValues } from '../AssignmentModal';
import { WorksiteLocationFormValues } from '../WorksiteLocation';
import { FinancialInfoSchedulerModel } from 'components/modals/FinancialInfoScheduleModal';

export const OVERTIME_MULTIPLIER = 1.5;
export const DT_PAY_RATE_MULTIPLIER = 2;

// Every math operation should be wrapped in getDecimalSafeValue
// due to inexact floats of js calculations
export class FinancialInfoCalculator {
  public static getBillRateValue = (billRate?: number | null): number =>
    billRate || 0;

  public static getPayRateValue = (payRate?: number | null): number =>
    payRate || 0;

  public static getProfitRateValue = (
    values?: FinancialInfoFormValues | FinancialInfoSchedulerModel,
    mspVmsValues?: CustomerMspOrVmsOverviewDto,
  ): number => {
    const mbpRate = values?.mbpRate || 0;
    const billRate = FinancialInfoCalculator.getBillRateValue(values?.billRate);
    const payRate = FinancialInfoCalculator.getPayRateValue(values?.payRate);
    const vmsMspRate = FinancialInfoCalculator.getVmsMspRateValue(mspVmsValues);
    const billRateVmsMspRate = getDecimalSafeValue(
      (billRate * vmsMspRate) / 100,
    );
    const payRateMbpRate = getDecimalSafeValue((payRate * mbpRate) / 100);

    return getDecimalSafeValue(
      billRate - (payRate + billRateVmsMspRate + payRateMbpRate),
    );
  };
  public static getMinimumBillRateForZeroProfit = (
    values?: FinancialInfoFormValues | FinancialInfoSchedulerModel,
    mspVmsValues?: CustomerMspOrVmsOverviewDto,
  ): number => {
    const mbpRate = values?.mbpRate || 0;
    const payRate = FinancialInfoCalculator.getPayRateValue(values?.payRate);
    const vmsMspRate = FinancialInfoCalculator.getVmsMspRateValue(mspVmsValues);

    const numerator = Math.round(payRate * (10000 + Math.round(mbpRate * 100)));
    const denominator = 10000 - Math.round(vmsMspRate * 100);

    const minimumBillRate = numerator / denominator;

    return getDecimalSafeValue(minimumBillRate);
  };

  public static getVmsMspRateValue = (
    mspVmsValues?: CustomerMspOrVmsOverviewDto,
  ): number => {
    if (!mspVmsValues) return 0;

    const { mspFee, vmsFee } = mspVmsValues;

    if (!!mspFee && !!toNumber(mspFee)) {
      return parseFloat(mspFee); // should be a number on BE
    }

    if (!!vmsFee) {
      return vmsFee;
    }

    return 0;
  };

  public static getVmsMspPortionValue = (
    billRateValue?: number | null,
    mspVmsValues?: CustomerMspOrVmsOverviewDto,
  ): number => {
    const billRate = FinancialInfoCalculator.getBillRateValue(billRateValue);
    const vmsMspRate = FinancialInfoCalculator.getVmsMspRateValue(mspVmsValues);

    return getDecimalSafeValue(
      getDecimalSafeValue(billRate * vmsMspRate) / 100,
    );
  };

  public static getMbpPortionValue = (
    payRateValue?: number | null,
    mbpRateValue?: number | null,
  ): number => {
    const payRate = FinancialInfoCalculator.getPayRateValue(payRateValue);
    const mbpRate = mbpRateValue || 0;
    return getDecimalSafeValue(getDecimalSafeValue(payRate * mbpRate) / 100);
  };

  public static getMarginPercentValue = (
    values?: FinancialInfoFormValues,
    mspVmsValues?: CustomerMspOrVmsOverviewDto,
  ): number => {
    const billRate = FinancialInfoCalculator.getBillRateValue(values?.billRate);

    if (billRate === 0) return 0;

    const profitRate = FinancialInfoCalculator.getProfitRateValue(
      values,
      mspVmsValues,
    );

    return getDecimalSafeValue(
      getDecimalSafeValue(profitRate / billRate) * 100,
    );
  };

  public static getMarkupPercentValue = (
    values?: FinancialInfoFormValues,
  ): number => {
    const billRate = FinancialInfoCalculator.getBillRateValue(values?.billRate);
    const payRate = FinancialInfoCalculator.getPayRateValue(values?.payRate);

    return getDecimalSafeValue(
      getDecimalSafeValue((billRate - payRate) / payRate) * 100,
    );
  };
  public static getInitialMbpRate = (
    workerInfo?: WorkerInfoFormValues,
    worksiteLocation?: WorksiteLocationFormValues,
    assignmentType?: AssignmentTypeEnum | null,
    priceAgreement?: BOUsersPriceAgreementRespModel,
  ): number => {
    const w2InfoList = priceAgreement?.w2InfoList;
    const isW2Assignment = assignmentType === AssignmentTypeEnum.EEAssignment;

    if (!isW2Assignment) {
      return priceAgreement?.icPrice || 0;
    }

    const w2InfoByWorkSiteWorkLocation =
      FinancialInfoCalculator.w2InfoByWorkSiteWorkLocation(
        w2InfoList,
        isW2Assignment,
        workerInfo,
        worksiteLocation,
      );

    return w2InfoByWorkSiteWorkLocation
      ? (w2InfoByWorkSiteWorkLocation.w2Price as number)
      : 0;
  };
  public static getWorkerLocation = (
    isW2Assignment: boolean,
    workerInfo?: WorkerInfoFormValues,
  ) => {
    const worker = workerInfo?.worker;
    const company = workerInfo?.company;

    return isW2Assignment
      ? {
          country: worker?.country || undefined,
          internationalStateName: worker?.internationalStateName || undefined,
          state: worker?.state,
          internationalCityName: worker?.internationalCityName || undefined,
          city: worker?.city || undefined,
          address: worker?.address || undefined,
          zip: worker?.zip || undefined,
        }
      : {
          country: company?.country || undefined,
          state: company?.state || undefined,
          internationalStateName: company?.internationalStateName || undefined,
          city: company?.city || undefined,
          internationalCityName: company?.internationalCityName || undefined,
          address: company?.address || undefined,
          zip: company?.zip || undefined,
        };
  };
  public static getWorksiteWorkLocation = (
    isW2Assignment: boolean,
    workerInfo?: WorkerInfoFormValues,
    worksiteLocation?: WorksiteLocationFormValues,
  ) => {
    const work = worksiteLocation?.work;
    return work?.locationSameAsMain
      ? FinancialInfoCalculator.getWorkerLocation(isW2Assignment, workerInfo)
      : {
          state: work?.state,
          city: work?.city,
          address: work?.address,
          zip: work?.zip,
        };
  };
  public static w2InfoByWorkSiteWorkLocation = (
    w2InfoList: PriceAgreementW2InfoResponseModel[] | undefined,
    isW2Assignment: boolean,
    workerInfo?: WorkerInfoFormValues,
    worksiteLocation?: WorksiteLocationFormValues,
  ) => {
    const workSiteWorkLocation =
      FinancialInfoCalculator.getWorksiteWorkLocation(
        isW2Assignment,
        workerInfo,
        worksiteLocation,
      );

    if (
      !w2InfoList?.length ||
      !workSiteWorkLocation?.state ||
      !isW2Assignment
    ) {
      return;
    }

    const w2InfoStateIds = w2InfoList.map((w2Info) => ({
      ...w2Info,
      placementStates: (w2Info.placementStates || []).map((state) => state.id),
    }));

    return w2InfoStateIds.find((w2Info) =>
      w2Info.placementStates.includes(workSiteWorkLocation.state),
    );
  };
  static getMBPRateValue = (
    isW2Assignment?: boolean,
    icPrice?: number,
    w2Price?: number,
  ) => {
    if (!isW2Assignment) return icPrice || 0;

    return w2Price || 0;
  };
  public static calculatePayRatePTOValue = (
    basePayRate?: number | null,
  ): number => basePayRate || 0;
  public static calculateBillRatePTOValue = (
    billRate?: number | null,
    mbpPortion?: number | null,
    isPaidByMember?: boolean,
  ): number | null => {
    if (isNil(isPaidByMember)) return null;

    return isPaidByMember ? (billRate || 0) + (mbpPortion || 0) : billRate || 0;
  };
}
